import { LOCALE_TAG, pickLocale, type Locale } from '@shh/shared';
import { setUiLocale } from '@shh/ui';

/**
 * Language of this device (1.19, Mike 12.10.: "Sprache pro Gerät, umschaltbar"). Chosen in
 * Einstellungen → Dieses Gerät, otherwise the browser's language, otherwise German.
 *
 * Read once at start: switching saves and reloads the app. That keeps every dictionary a plain
 * constant (`FEEDBACK_TEXT[locale]`) instead of a hook in hundreds of places.
 */
const KEY = 'shh.locale';

function stored(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null; // private mode: follow the browser
  }
}

function initial(): Locale {
  const s = stored();
  if (s) return pickLocale(s);
  return typeof navigator === 'undefined' ? 'de' : pickLocale(navigator.language);
}

export const locale: Locale = initial();
/** 'de-DE' / 'en-GB' for Intl – dates and numbers follow the device language. */
export const localeTag = LOCALE_TAG[locale];

if (typeof document !== 'undefined') document.documentElement.lang = locale;
// the building blocks of @shh/ui speak the same language
setUiLocale(locale);

/** Saves the device language and restarts the app in it. */
export function setDeviceLocale(next: Locale) {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* private mode: this session only – the reload falls back to the browser */
  }
  if (next !== locale) window.location.reload();
}

export const dateFormat = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(localeTag, opts);
export const numberFormat = (opts?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(localeTag, opts);
